import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Upload,
  FileText,
  CheckCircle2,
  Loader2,
  X,
  AlertCircle,
  FolderOpen,
  ArrowRight,
  RotateCcw,
  ShieldCheck,
  CloudUpload,
  Sparkles,
  FileSpreadsheet,
  FileImage,
} from 'lucide-react';
import { formatFileSize } from '@/utils/kbUtils';
import KBPageHeader from '@/components/knowledge/KBPageHeader';
import FolderManagerPanel from '@/components/knowledge/FolderManagerPanel';
import { documentService, brainConfigService } from '@/services/kbService';
import { fileUpload } from '@/utils/ERPUtils';
import { BRAND_NAME } from '@/utils/branding';
import { useAuth } from '@/context/AuthContext';
import { Progress } from '@/components/ui/progress';

import { kbUploadStore } from '@/stores/kbUploadStore';
import { logger } from '@/utils/logger';
import {
  cloudSourceService,
  CloudSourceListRow,
} from '@/services/cloudSourceService';
// ─── Types ───────────────────────────────────────────────────────────────────

interface FileQueueItem {
  id: string;
  file: File;
  status: 'pending' | 'validating' | 'valid' | 'invalid' | 'uploading' | 's3_uploaded' | 'queued' | 'error';
  progress: number;
  error?: string;
  /** Errors returned from pre-validation */
  validationErrors?: string[];
  /** The file_url returned after S3 upload */
  fileUrl?: string;
  /** The document_id returned after batch queue */
  documentId?: string;
}

// ─── Allowed file types (client-side check) ──────────────────────────────────

// Fallbacks only. The live limits come from Orgain Brain Settings
// (allowed_file_types / max_file_size_mb), fetched on mount. These apply
// until that fetch resolves, or if it fails / returns empty, so the page
// keeps working offline-of-settings.
const DEFAULT_ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.txt', '.md', '.rtf',
  '.xlsx', '.xls', '.csv', '.pptx', '.ppt',
  '.png', '.jpg', '.jpeg', '.gif', '.tiff', '.bmp'
];
const DEFAULT_MAX_FILE_SIZE_MB = 100;

function parseAllowedExtensions(raw?: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean)
    .map(s => (s.startsWith('.') ? s : `.${s}`));
}

function getFileExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.substring(idx).toLowerCase() : '';
}

function isAllowedFile(
  file: File,
  allowedExtensions: string[],
  maxFileSizeMb: number,
): { ok: boolean; reason?: string } {
  const ext = getFileExtension(file.name);
  if (!allowedExtensions.includes(ext)) {
    return { ok: false, reason: `Unsupported file type: ${ext}` };
  }
  if (file.size > maxFileSizeMb * 1024 * 1024) {
    return { ok: false, reason: `File too large (${formatFileSize(file.size)}). Max ${maxFileSizeMb}MB.` };
  }
  return { ok: true };
}

// ─── Component ───────────────────────────────────────────────────────────────

const KBUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Restore persisted snapshot (if any) so navigating back preserves state
  const snapshot = useMemo(() => kbUploadStore.restore(), []);

  // Wizard step: 0=Select, 1=Pre-validate, 2=Upload & Queue, 3=Confirmation
  const [currentStep, setCurrentStep] = useState(snapshot?.currentStep ?? 0);

  // Upload limits sourced from Orgain Brain Settings (single source of
  // truth), with the module-level defaults as fallback until they load.
  const [allowedExtensions, setAllowedExtensions] = useState<string[]>(DEFAULT_ALLOWED_EXTENSIONS);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState<number>(DEFAULT_MAX_FILE_SIZE_MB);
  useEffect(() => {
    let cancelled = false;
    brainConfigService.get()
      .then(settings => {
        if (cancelled) return;
        const exts = parseAllowedExtensions(settings.allowed_file_types);
        if (exts.length) setAllowedExtensions(exts);
        if (settings.max_file_size_mb && settings.max_file_size_mb > 0) {
          setMaxFileSizeMb(settings.max_file_size_mb);
        }
      })
      .catch(err => {
        // Non-fatal: keep the defaults so uploads still work.
        logger.warn('Failed to load upload limits from Brain Settings; using defaults', err);
      });
    return () => { cancelled = true; };
  }, []);

  // File queue
  const [fileQueue, setFileQueue] = useState<FileQueueItem[]>(snapshot?.fileQueue ?? []);
  const fileQueueRef = useRef<FileQueueItem[]>(fileQueue);
  useEffect(() => { fileQueueRef.current = fileQueue; }, [fileQueue]);

  // Processing flags
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Drag state
  const [isDragOver, setIsDragOver] = useState(false);

  // Error banner
  const [error, setError] = useState<string | null>(snapshot?.error ?? null);

  // Upload summary (Step 3)
  const [uploadSummary, setUploadSummary] = useState<{
    uploaded: number;
    queued: number;
    failed: number;
  } | null>(snapshot?.uploadSummary ?? null);

  // Persist state to module-level store so it survives navigation.
  // Don't persist the confirmation step (3) - once files are queued,
  // returning to this page should show a fresh upload view.
  useEffect(() => {
    if (currentStep < 3) {
      kbUploadStore.save({ fileQueue, currentStep, uploadSummary, error });
    }
  }, [fileQueue, currentStep, uploadSummary, error]);

  // ─── Connected cloud sources (mutual-exclusivity check only; the cloud
  // import surface itself lives at /dashboard/admin/cloud-import) ───────────
  const [cloudSources, setCloudSources] = useState<CloudSourceListRow[]>([]);
  const [cloudLoading, setCloudLoading] = useState(true);

  const reloadCloudSources = useCallback(async () => {
    try {
      const res = await cloudSourceService.listMySources();
      setCloudSources(res.sources || []);
    } catch (err: unknown) {
      logger.warn('Failed to load cloud sources', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCloudLoading(true);
      try {
        await reloadCloudSources();
      } finally {
        if (!cancelled) setCloudLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadCloudSources]);

  // "connected" means OAuth completed - without this the button flips to
  // "<Provider> connected" the moment a placeholder row is created, even if
  // the user closed the popup and the row is still Pending Authorization.
  const hasDriveSource = cloudSources.some(
    (s) => s.provider === 'Google Drive' && s.connection_status === 'Connected',
  );
  const hasSharePointSource = cloudSources.some(
    (s) => s.provider === 'SharePoint' && s.connection_status === 'Connected',
  );

  // Device upload and cloud import are mutually exclusive: a connected cloud
  // account (SharePoint/Google) blocks device upload, and files queued for
  // device upload block cloud import. A pre-existing device queue wins so the
  // two rules can't deadlock the user out of both surfaces.
  const hasConnectedCloud = hasDriveSource || hasSharePointSource;
  const hasDeviceQueue = fileQueue.length > 0;

  // A pre-existing device queue wins so the rule can't deadlock the user
  // out of both surfaces (matches the old tab-switching behaviour).
  const deviceUploadBlocked = hasConnectedCloud && !hasDeviceQueue;

  // ─── Step 0: File selection ────────────────────────────────────────────────

  const handleMultiFileSelect = useCallback((files: FileList | File[]) => {
    const newItems: FileQueueItem[] = Array.from(files).map(file => {
      const check = isAllowedFile(file, allowedExtensions, maxFileSizeMb);
      return {
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        file,
        status: check.ok ? ('pending' as const) : ('invalid' as const),
        progress: 0,
        error: check.ok ? undefined : check.reason,
        validationErrors: check.ok ? undefined : (check.reason ? [check.reason] : undefined),
      };
    });
    setFileQueue(prev => [...prev, ...newItems]);
  }, [allowedExtensions, maxFileSizeMb]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleMultiFileSelect(files);
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleMultiFileSelect(files);
    }
    e.target.value = '';
  };

  const removeFromQueue = (id: string) => {
    setFileQueue(prev => prev.filter(item => item.id !== id));
  };

  const clearQueue = () => {
    setFileQueue([]);
    setCurrentStep(0);
    setError(null);
    setUploadSummary(null);
    kbUploadStore.clear();
  };

  // ─── Step 1: Pre-validate ──────────────────────────────────────────────────

  // Send only the SHA-256 - never the file bytes. Pre-validate used to also
  // ship each file as base64 so the server could extract its text and check for
  // a text-level duplicate. That meant uploading every file TWICE (once to
  // validate, once to upload, +33% for base64) and made the server parse each
  // one synchronously, which ran past its 120s worker timeout and returned 502.
  // The upload endpoints run the identical text check before ingesting, so a
  // text-level duplicate is still caught - just at upload rather than here.
  const computeHash = async (file: File): Promise<{ content_hash: string }> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const content_hash = Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    return { content_hash };
  };

  // Returns true when the LLM keys the pipeline needs are configured. Shows a
  // clear toast and returns false when they're missing. Fails OPEN on a check
  // error (don't block on a transient failure - the server-side guard in
  // batch_upload_documents still rejects the actual upload if keys are missing).
  const ensureLlmConfigured = async (): Promise<boolean> => {
    try {
      const llm = await documentService.checkLlmConfiguration();
      if (!llm.ok) {
        // Neutral title - the key may be missing OR configured-but-invalid;
        // the backend message says which.
        toast.error('LLM API key issue', {
          description:
            llm.message ||
            `Missing or invalid key for: ${(llm.providers_missing || []).join(', ')}. ` +
              'Set a valid key in KB Settings → API Keys, then try again.',
        });
        return false;
      }
      return true;
    } catch (err) {
      logger.error('LLM configuration check failed', err);
      return true; // fail-open; server-side guard still applies
    }
  };

  const preValidateFiles = async () => {
    const pendingFiles = fileQueue.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast.info('No files to validate');
      return;
    }

    // Gate: the ingestion pipeline needs LLM API keys (entity extraction,
    // relationship extraction, embeddings, …). Surface a clear message up
    // front instead of letting processing fail in a background worker.
    if (!(await ensureLlmConfigured())) return;

    setIsValidating(true);
    setError(null);
    setCurrentStep(1);

    // Mark all pending as validating
    setFileQueue(prev =>
      prev.map(f =>
        f.status === 'pending' ? { ...f, status: 'validating' as const, progress: 50 } : f
      )
    );

    try {
      // Compute SHA-256 hash + (for smaller files) base64 content in one read,
      // so pre-validate runs both the byte-hash and text-content duplicate
      // checks.
      const filesInfo = await Promise.all(
        pendingFiles.map(async f => ({
          filename: f.file.name,
          file_size: f.file.size,
          ...(await computeHash(f.file)),
        }))
      );

      const response = await documentService.preValidateDocuments(filesInfo);

      // Map results back to queue items
      setFileQueue(prev =>
        prev.map(f => {
          if (f.status !== 'validating') return f;

          const result = response.results?.find(
            r => r.filename === f.file.name
          );

          if (!result) {
            // File not found in results - keep as pending
            return { ...f, status: 'pending' as const, progress: 0 };
          }

          if (result.valid) {
            return {
              ...f,
              status: 'valid' as const,
              progress: 100,
              validationErrors: undefined,
              error: undefined,
            };
          } else {
            return {
              ...f,
              status: 'invalid' as const,
              progress: 0,
              validationErrors: result.errors,
              error: result.errors.join('; '),
            };
          }
        })
      );

      const validResultCount = response.valid_count;
      const invalidResultCount = response.invalid_count;

      if (invalidResultCount > 0) {
        toast.warning(
          `Validation complete: ${validResultCount} valid, ${invalidResultCount} invalid`
        );
      } else {
        toast.success(`All ${validResultCount} files passed validation`);
      }
    } catch (err: any) {
      logger.error('Pre-validation failed:', err);
      const errMsg = err.response?.data?.detail || err.message || 'Pre-validation failed';
      setError(errMsg);
      toast.error('Validation failed', { description: errMsg });

      // Reset validating items back to pending
      setFileQueue(prev =>
        prev.map(f =>
          f.status === 'validating' ? { ...f, status: 'pending' as const, progress: 0 } : f
        )
      );
      setCurrentStep(0);
    } finally {
      setIsValidating(false);
    }
  };

  // ─── Step 2: Upload to S3 then batch queue ────────────────────────────────

  const uploadAndQueue = async () => {
    const validFiles = fileQueue.filter(f => f.status === 'valid');
    if (validFiles.length === 0) {
      toast.info('No valid files to upload');
      return;
    }

    // Re-check keys before pushing anything to S3 - config may have changed
    // since validation, and we don't want files uploaded only to fail queuing.
    if (!(await ensureLlmConfigured())) return;

    setIsUploading(true);
    setError(null);
    setCurrentStep(2);

    let s3SuccessCount = 0;
    let s3FailCount = 0;

    // Track successfully uploaded items locally to avoid stale ref issues
    const successfulUploads: Array<{ id: string; file: File; fileUrl: string }> = [];

    // Phase 1: Upload each file to S3/Frappe
    for (const item of validFiles) {
      // Mark as uploading
      setFileQueue(prev =>
        prev.map(f =>
          f.id === item.id ? { ...f, status: 'uploading' as const, progress: 30 } : f
        )
      );

      try {
        const result = await fileUpload(item.file, maxFileSizeMb, { folderToBeUploaded: 'knowledgebase', isPrivate: true, uploadedBy: user?.email });

        if (result.error || !result.url) {
          throw new Error(result.error || 'Upload returned no URL');
        }

        // Mark as s3_uploaded
        setFileQueue(prev =>
          prev.map(f =>
            f.id === item.id
              ? { ...f, status: 's3_uploaded' as const, progress: 60, fileUrl: result.url }
              : f
          )
        );
        successfulUploads.push({ id: item.id, file: item.file, fileUrl: result.url });
        s3SuccessCount++;
      } catch (err: any) {
        logger.error(`Failed to upload ${item.file.name} to S3:`, err);
        setFileQueue(prev =>
          prev.map(f =>
            f.id === item.id
              ? {
                  ...f,
                  status: 'error' as const,
                  progress: 0,
                  error: err.message || 'S3 upload failed',
                }
              : f
          )
        );
        s3FailCount++;
      }
    }

    if (s3SuccessCount === 0) {
      // setError('All file uploads failed. Please try again.');
      setIsUploading(false);
      toast.error('All uploads failed');
      return;
    }

    // Phase 2: Batch queue all successfully uploaded files (use local array, not stale ref)
    const uploadedItems = successfulUploads;

    // Mark all as queuing
    setFileQueue(prev =>
      prev.map(f =>
        f.status === 's3_uploaded' ? { ...f, progress: 80 } : f
      )
    );

    try {
      const documents = uploadedItems.map(f => ({
        file_url: f.fileUrl!,
        title: f.file.name.replace(/\.[^.]+$/, ''),
      }));

      const batchResponse = await documentService.batchUploadDocuments(documents);

      // Map batch results back to queue items
      setFileQueue(prev =>
        prev.map(f => {
          if (f.status !== 's3_uploaded') return f;

          const result = batchResponse.results?.find(
            r => r.filename === f.file.name || r.title === f.file.name.replace(/\.[^.]+$/, '')
          );

          if (result && result.status === 'queued') {
            return {
              ...f,
              status: 'queued' as const,
              progress: 100,
              documentId: result.document_id,
            };
          } else if (result) {
            return {
              ...f,
              status: 'error' as const,
              progress: 0,
              error: result.message || `Queue failed: ${result.status}`,
            };
          }
          // No matching result - mark as error
          return {
            ...f,
            status: 'error' as const,
            progress: 0,
            error: 'No batch result returned for this file',
          };
        })
      );

      setUploadSummary({
        uploaded: s3SuccessCount,
        queued: batchResponse.queued_count || 0,
        failed: (batchResponse.failed_count || 0) + s3FailCount,
      });

      toast.success(
        `Upload complete: ${batchResponse.queued_count} files queued for processing`
      );

      // Move to confirmation step and clear the persistent store so
      // navigating away and back shows a fresh upload view.
      setCurrentStep(3);
      kbUploadStore.clear();
    } catch (err: any) {
      logger.error('Batch queue failed:', err);
      const errMsg = err.response?.data?.detail || err.message || 'Batch queue failed';
      setError(errMsg);
      toast.error('Batch queue failed', { description: errMsg });

      // Mark s3_uploaded items as error
      setFileQueue(prev =>
        prev.map(f =>
          f.status === 's3_uploaded'
            ? { ...f, status: 'error' as const, progress: 0, error: errMsg }
            : f
        )
      );
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Reset for "Upload More" ───────────────────────────────────────────────

  const handleReset = () => {
    setCurrentStep(0);
    setFileQueue([]);
    setError(null);
    setUploadSummary(null);
    kbUploadStore.clear();
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  // ─── Derived counts ───────────────────────────────────────────────────────

  const validCount = fileQueue.filter(f => f.status === 'valid').length;
  const invalidCount = fileQueue.filter(f => f.status === 'invalid').length;
  const pendingCount = fileQueue.filter(f => f.status === 'pending').length;
  const errorCount = fileQueue.filter(f => f.status === 'error').length;

  // ─── Status helpers ────────────────────────────────────────────────────────

  const getStatusBadgeVariant = (status: FileQueueItem['status']): 'secondary' | 'default' | 'destructive' | 'outline' => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'validating': return 'default';
      case 'valid': return 'outline';
      case 'invalid': return 'destructive';
      case 'uploading': return 'default';
      case 's3_uploaded': return 'default';
      case 'queued': return 'default';
      case 'error': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusText = (status: FileQueueItem['status']) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'validating': return 'Validating...';
      case 'valid': return 'Valid';
      case 'invalid': return 'Invalid';
      case 'uploading': return 'Uploading...';
      case 's3_uploaded': return 'Uploaded';
      case 'queued': return 'Queued';
      case 'error': return 'Failed';
      default: return status;
    }
  };

  const getStatusIcon = (status: FileQueueItem['status']) => {
    switch (status) {
      case 'validating':
      case 'uploading':
        return <Loader2 className="h-3 w-3 mr-1 animate-spin" />;
      case 'valid':
      case 'queued':
      case 's3_uploaded':
        return <CheckCircle2 className="h-3 w-3 mr-1" />;
      case 'invalid':
      case 'error':
        return <AlertCircle className="h-3 w-3 mr-1" />;
      default:
        return null;
    }
  };

  // ─── Step definitions ──────────────────────────────────────────────────────

  const steps = [
    { title: 'Select', description: 'Add files' },
    { title: 'Pre-validate', description: 'Server check' },
    { title: 'Upload & Queue', description: 'Upload files' },
    { title: 'Confirmation', description: 'View results' },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="dark bg-background text-foreground min-h-full space-y-6 max-w-full px-4 sm:px-8 py-4 sm:py-6">
      {/* Header */}
      <KBPageHeader
        icon={<Upload className="h-7 w-7" />}
        title="Manual Ingestion Hub"
        description="Secure entry point for organizational intelligence. Documents are validated, uploaded, and indexed across the vector store, knowledge graph, and relational database."
      />

      {/* Error Alert */}
      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="flex items-center gap-2 py-4">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <span className="text-destructive">{error}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>
              <X className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Folder Manager rail (relocated from its old standalone page) +
          the upload flow. On small screens the rail drops below the flow. */}
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[290px_minmax(0,1fr)] lg:items-start">
      <FolderManagerPanel className="order-2 lg:order-1" />
      {/* Bordered to visually pair with the Folder Manager card on the left */}
      <div className="order-1 min-w-0 space-y-6 rounded-lg border border-border bg-card p-4 sm:p-6 lg:order-2">

      {/* Step Indicator - only relevant to the device-upload flow, so it
          shows once we're past step 0 or while the "Upload from device"
          tab is active. Hidden during the availability call so it doesn't
          flash in and out on first load. */}
      {!cloudLoading && (currentStep > 0 || !deviceUploadBlocked) && (
      <div className="flex items-center justify-center px-4 lg:px-16">
        <div className="flex items-center justify-between w-full max-w-3xl">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center text-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep > index
                      ? 'bg-emerald-500 text-white'
                      : currentStep === index
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {currentStep > index ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <div className="hidden sm:block mt-2">
                  <p
                    className={`text-sm font-medium ${
                      currentStep >= index ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {step.title}
                  </p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 sm:mx-4 ${
                    currentStep > index ? 'bg-emerald-500' : 'bg-muted'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          STEP 0: SELECT FILES - device upload only. "Import from cloud" now
          lives at /dashboard/admin/cloud-import; the mutual-exclusivity rule
          (connected cloud account blocks device upload) is enforced here.
          ═══════════════════════════════════════════════════════════════════════ */}
      {currentStep === 0 && cloudLoading && (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      )}
      {currentStep === 0 && !cloudLoading && deviceUploadBlocked && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed bg-muted/30 px-6 py-16 text-center">
          <CloudUpload className="h-8 w-8 text-muted-foreground" />
          <h2 className="text-base font-semibold">Cloud import is active</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Documents are ingested through your connected SharePoint / Google Drive
            account, so device upload is disabled. Disconnect the cloud account to
            upload from this device.
          </p>
          <Button variant="outline" onClick={() => navigate('/dashboard/admin/cloud-import')}>
            <CloudUpload className="mr-2 h-4 w-4" />
            Manage Cloud Import
          </Button>
        </div>
      )}
      {currentStep === 0 && !cloudLoading && !deviceUploadBlocked && (
        <div className="space-y-6">
          {/* Inbound portal drop zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-upload')?.click()}
            className={'border-2 border-dashed rounded-xl px-6 py-12 sm:py-16 text-center transition-colors cursor-pointer ' + (isDragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50')}
          >
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-xl border border-border bg-primary/10">
              <Upload className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Inbound Intelligence Portal</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Drag and drop files or browse your local system
            </p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">
              PDF &middot; Word &middot; Excel &middot; PowerPoint &middot; CSV &middot; Images &mdash; max {maxFileSizeMb}MB per file
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              You can select multiple files for batch processing
            </p>
            <div className="mt-8 flex items-center justify-center gap-4 sm:gap-6">
              <div className="flex flex-col items-center gap-2">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-muted/40">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Documents</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-muted/40">
                  <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Datasets</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-muted/40">
                  <FileImage className="h-5 w-5 text-muted-foreground" />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Images</span>
              </div>
            </div>
            <input
              type="file"
              accept={allowedExtensions.join(',')}
              className="hidden"
              id="file-upload"
              multiple
              onChange={handleFileInputChange}
            />
          </div>

          {/* Live processing stream (staged file queue) */}
          {fileQueue.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-semibold">Live Processing Stream</h2>
                    <Badge variant="outline" className="font-mono text-[10px] tracking-wider bg-primary/10 text-primary border-primary/30">
                      {fileQueue.length} STAGED
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    {pendingCount > 0 && (
                      <span className="text-muted-foreground">
                        Pending: <strong>{pendingCount}</strong>
                      </span>
                    )}
                    {invalidCount > 0 && (
                      <span className="text-red-400">
                        Invalid: <strong>{invalidCount}</strong>
                      </span>
                    )}
                  </div>
                </div>

                <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_110px_170px_40px] gap-2 px-4 py-2 border-b border-border bg-muted/30 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <span>Payload Identifier</span>
                  <span>Source Volume</span>
                  <span>Status Protocol</span>
                  <span />
                </div>

                <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
                  {fileQueue.map(item => (
                    <div key={item.id} className="px-4 py-3 sm:grid sm:grid-cols-[minmax(0,1fr)_110px_170px_40px] sm:gap-2 sm:items-center">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{item.file.name}</p>
                          {item.error && (
                            <p className="text-xs text-destructive truncate">{item.error}</p>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground pl-12 sm:pl-0 mt-1 sm:mt-0 font-mono">
                        {formatFileSize(item.file.size)}
                      </p>
                      <div className="pl-12 sm:pl-0 mt-1 sm:mt-0">
                        <Badge variant={getStatusBadgeVariant(item.status)}>
                          {getStatusIcon(item.status)}
                          {getStatusText(item.status)}
                        </Badge>
                      </div>
                      <div className="pl-12 sm:pl-0 mt-1 sm:mt-0 flex sm:justify-end">
                        <button
                          onClick={() => removeFromQueue(item.id)}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label="Remove from queue"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Staging footer */}
          {fileQueue.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-border bg-card/60 px-4 py-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Total Estimated Payload</p>
                <p className="text-lg font-bold font-mono">
                  {formatFileSize(fileQueue.reduce((sum, f) => sum + f.file.size, 0))}
                </p>
              </div>
              <p className="text-sm text-muted-foreground sm:ml-6">
                {fileQueue.length} file{fileQueue.length !== 1 ? 's' : ''} staged for ingestion
              </p>
              <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
                    >
                      Cancel Stream
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="dark bg-background text-foreground">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear upload queue?</AlertDialogTitle>
                      <AlertDialogDescription>
                        All files in the queue will be removed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={clearQueue}>Clear All</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {pendingCount > 0 && (
                  <Button onClick={preValidateFiles}>
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Validate &amp; Ingest ({pendingCount})
                  </Button>
                )}
                {pendingCount === 0 && validCount > 0 && (
                  <Button onClick={() => setCurrentStep(1)}>
                    <ArrowRight className="mr-2 h-4 w-4" />
                    Continue ({validCount})
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          STEP 1: PRE-VALIDATE RESULTS
          ═══════════════════════════════════════════════════════════════════════ */}
      {currentStep === 1 && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold">Validation Results</h2>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentStep(0)}>
                Back
              </Button>
              {/* Allow removing invalid and re-validating */}
              {invalidCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFileQueue(prev => prev.filter(f => f.status !== 'invalid'));
                    toast.info(`Removed ${invalidCount} invalid file(s)`);
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Remove Invalid ({invalidCount})
                </Button>
              )}
              <Button
                size="sm"
                disabled={validCount === 0 || isValidating}
                onClick={uploadAndQueue}
              >
                {isValidating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    <CloudUpload className="mr-2 h-4 w-4" />
                    Continue ({validCount})
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Summary bar */}
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="text-emerald-400 font-medium">
              Valid: {validCount}
            </span>
            {invalidCount > 0 && (
              <span className="text-red-400 font-medium">
                Invalid: {invalidCount}
              </span>
            )}
            {errorCount > 0 && (
              <span className="text-red-400 font-medium">
                Errors: {errorCount}
              </span>
            )}
          </div>

          {/* File list with validation results */}
          <div className="border rounded-lg divide-y max-h-[500px] overflow-y-auto">
            {fileQueue.map(item => (
              <div
                key={item.id}
                className={`p-3 ${
                  item.status === 'valid'
                    ? 'bg-emerald-500/10'
                    : item.status === 'invalid'
                      ? 'bg-red-500/10'
                      : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  {item.status === 'valid' ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  ) : item.status === 'invalid' || item.status === 'error' ? (
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Loader2 className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5 animate-spin" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{item.file.name}</p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant={getStatusBadgeVariant(item.status)}>
                          {getStatusIcon(item.status)}
                          {getStatusText(item.status)}
                        </Badge>
                        {(item.status === 'invalid' || item.status === 'error') && (
                          <button
                            onClick={() => removeFromQueue(item.id)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatFileSize(item.file.size)}
                    </p>
                    {item.validationErrors && item.validationErrors.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {item.validationErrors.map((err, i) => (
                          <p key={i} className="text-xs text-destructive">
                            {err}
                          </p>
                        ))}
                      </div>
                    )}
                    {item.error && !item.validationErrors?.length && (
                      <p className="text-xs text-destructive mt-1">{item.error}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          STEP 2: UPLOAD & QUEUE (progress view)
          ═══════════════════════════════════════════════════════════════════════ */}
      {currentStep === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">Uploading Files</h2>
            {isUploading && (
              <Badge variant="default" className="animate-pulse">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Processing...
              </Badge>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            Files are being uploaded and queued for background AI processing. Please do not close this page.
          </p>

          {/* File list with upload progress */}
          <div className="border rounded-lg divide-y max-h-[500px] overflow-y-auto">
            {fileQueue
              .filter(f => f.status !== 'invalid')
              .map(item => (
                <div key={item.id} className="p-3">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium truncate">{item.file.name}</p>
                        <Badge variant={getStatusBadgeVariant(item.status)}>
                          {getStatusIcon(item.status)}
                          {getStatusText(item.status)}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatFileSize(item.file.size)}
                      </p>
                      {item.progress > 0 && item.status !== 'queued' && item.status !== 'error' && (
                        <Progress value={item.status === 's3_uploaded' ? 100 : item.progress} className="h-1.5 mt-2" />
                      )}
                      {item.status === 'queued' && (
                        <p className="text-xs text-emerald-400 mt-1">
                          Queued for background processing
                        </p>
                      )}
                      {item.error && (
                        <p className="text-xs text-destructive mt-1">{item.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          STEP 3: CONFIRMATION
          ═══════════════════════════════════════════════════════════════════════ */}
      {currentStep === 3 && (
        <div className="space-y-6">
          {/* Summary card */}
          <Card className="border-emerald-500/30 bg-emerald-500/10">
            <CardContent className="py-8">
              <div className="text-center mb-4">
                <Sparkles className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-emerald-300">
                  {BRAND_NAME} is now analyzing your documents
                </h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                  Your {uploadSummary?.queued || 0} document{(uploadSummary?.queued || 0) !== 1 ? 's have' : ' has'} been
                  queued for processing. Once complete, {(uploadSummary?.queued || 0) !== 1 ? 'they' : 'it'} will be
                  available in your Knowledge Base for you to browse and interact with.
                </p>
              </div>
              {uploadSummary && (
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="text-center p-3 bg-card rounded-lg border border-border">
                    <p className="text-2xl font-bold text-blue-400">{uploadSummary.uploaded}</p>
                    <p className="text-xs text-muted-foreground mt-1">Files Uploaded</p>
                  </div>
                  <div className="text-center p-3 bg-card rounded-lg border border-border">
                    <p className="text-2xl font-bold text-emerald-400">{uploadSummary.queued}</p>
                    <p className="text-xs text-muted-foreground mt-1">Queued for Processing</p>
                  </div>
                  <div className="text-center p-3 bg-card rounded-lg border border-border">
                    <p className="text-2xl font-bold text-red-400">{uploadSummary.failed}</p>
                    <p className="text-xs text-muted-foreground mt-1">Failed</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Per-file results */}
          <div>
            <h3 className="text-sm font-semibold mb-3">File Results</h3>
            <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
              {fileQueue
                .filter(f => f.status !== 'invalid')
                .map(item => (
                  <div
                    key={item.id}
                    className={`p-3 ${
                      item.status === 'queued' ? 'bg-emerald-500/[0.07]' : item.status === 'error' ? 'bg-red-500/[0.07]' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {item.status === 'queued' ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium truncate">{item.file.name}</p>
                          <Badge variant={getStatusBadgeVariant(item.status)}>
                            {getStatusIcon(item.status)}
                            {getStatusText(item.status)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(item.file.size)}
                          </p>
                          {item.documentId && (
                            <p className="text-xs text-muted-foreground">
                              ID: <span className="font-mono">{item.documentId}</span>
                            </p>
                          )}
                        </div>
                        {item.error && (
                          <p className="text-xs text-destructive mt-1">{item.error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <Button variant="outline" onClick={handleReset} className="w-full sm:w-auto">
              <RotateCcw className="mr-2 h-4 w-4" />
              Upload More
            </Button>
            <Button onClick={() => navigate('/dashboard/admin/knowledge-base')} className="w-full sm:w-auto">
              Go to Knowledge Base
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      </div>
      </div>
    </div>
  );
};

export default KBUploadPage;
